import { useEffect, useMemo, useState } from 'react';
import api, { errMsg, branchLabel } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { ClipboardList, Loader2 } from 'lucide-react';

export default function Opname() {
  const { user, branches } = useAuth();
  const isAdmin = user.role === 'admin';
  const [branch, setBranch] = useState(isAdmin ? (branches[0]?.id || 'balaraja') : user.branch);
  const [products, setProducts] = useState(null);
  const [actuals, setActuals] = useState({});
  const [notes, setNotes] = useState('');
  const [adjust, setAdjust] = useState(true);
  const [history, setHistory] = useState([]);
  const [saving, setSaving] = useState(false);

  const loadHistory = () => api.get('/opname').then((r) => setHistory(r.data)).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  useEffect(() => {
    setProducts(null);
    setActuals({});
    api.get('/products').then((r) => {
      setProducts(r.data);
      const init = {};
      r.data.forEach((p) => p.variants.forEach((v) => { init[`${p.id}|${v.size}`] = String(v.stock?.[branch] ?? 0); }));
      setActuals(init);
    }).catch((e) => toast.error(errMsg(e)));
  }, [branch]);

  const rows = useMemo(() => {
    if (!products) return [];
    return products.flatMap((p) => p.variants.map((v) => ({
      key: `${p.id}|${v.size}`, product_id: p.id, article: p.article, brand: p.brand,
      size: v.size, system: v.stock?.[branch] ?? 0,
    })));
  }, [products, branch]);

  const changed = rows.filter((r) => Number(actuals[r.key] ?? r.system) !== r.system);

  const submit = async () => {
    if (changed.length === 0) return toast.info('Tidak ada perbedaan stok');
    setSaving(true);
    try {
      await api.post('/opname', {
        branch, notes, adjust,
        items: changed.map((r) => ({ product_id: r.product_id, size: r.size, actual_qty: Number(actuals[r.key]) || 0 })),
      });
      toast.success(`Opname tersimpan — ${changed.length} item ${adjust ? 'disesuaikan' : 'dicatat'}`);
      setNotes('');
      const r = await api.get('/products');
      setProducts(r.data);
      const init = {};
      r.data.forEach((p) => p.variants.forEach((v) => { init[`${p.id}|${v.size}`] = String(v.stock?.[branch] ?? 0); }));
      setActuals(init);
      loadHistory();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5 animate-fade-up" data-testid="opname-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">Stok Opname</h2>
          <p className="text-sm text-muted-foreground">Hitung stok fisik, sistem hitung selisih otomatis</p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            {branches.map((b) => (
              <button key={b.id} onClick={() => setBranch(b.id)} data-testid={`opname-branch-${b.id}`}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border ${branch === b.id ? 'bg-brand text-white border-brand' : 'bg-surface border-line text-muted-foreground'}`}>
                {b.name}
              </button>
            ))}
          </div>
        )}
      </div>

      {!products ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 text-brand animate-spin" /></div>
      ) : (
        <div className="lodi-card overflow-hidden" data-testid="opname-table">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-muted-foreground uppercase tracking-wide border-b border-line bg-elevated/50">
                  <th className="px-4 py-3">Artikel</th>
                  <th className="px-4 py-3">Ukuran</th>
                  <th className="px-4 py-3 text-center">Stok Sistem</th>
                  <th className="px-4 py-3 text-center">Stok Fisik</th>
                  <th className="px-4 py-3 text-center">Selisih</th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-muted-foreground">Belum ada produk</td></tr>}
                {rows.map((r) => {
                  const actual = Number(actuals[r.key] ?? r.system);
                  const variance = actual - r.system;
                  return (
                    <tr key={r.key} className="border-b border-line/50 last:border-0" data-testid={`opname-row-${r.product_id}-${r.size}`}>
                      <td className="px-4 py-2.5 font-semibold">{r.article}<span className="block text-xs text-muted-foreground font-normal">{r.brand}</span></td>
                      <td className="px-4 py-2.5 font-mono">{r.size}</td>
                      <td className="px-4 py-2.5 text-center font-semibold">{r.system}</td>
                      <td className="px-4 py-2.5 text-center">
                        <input type="number" min="0" value={actuals[r.key] ?? ''} onChange={(e) => setActuals({ ...actuals, [r.key]: e.target.value })}
                          className="lodi-input w-20 text-center mx-auto" data-testid={`opname-actual-${r.product_id}-${r.size}`} />
                      </td>
                      <td className={`px-4 py-2.5 text-center font-bold ${variance > 0 ? 'text-emerald-400' : variance < 0 ? 'text-brand' : 'text-muted-foreground'}`}>
                        {variance > 0 ? `+${variance}` : variance}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="p-4 border-t border-line space-y-3">
            <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Catatan opname (opsional)" className="lodi-input" data-testid="opname-notes-input" />
            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={adjust} onChange={(e) => setAdjust(e.target.checked)} className="w-4 h-4 accent-[#FF3823]" data-testid="opname-adjust-checkbox" />
              Sesuaikan stok sistem dengan stok fisik
            </label>
            <button onClick={submit} disabled={saving} data-testid="opname-submit-button"
              className="w-full sm:w-auto px-6 py-3 rounded-xl bg-brand hover:bg-branddark disabled:opacity-40 text-white font-display font-bold flex items-center justify-center gap-2 transition-all active:scale-[0.98]">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ClipboardList className="w-4 h-4" />}
              SIMPAN OPNAME {changed.length > 0 && `(${changed.length} berubah)`}
            </button>
          </div>
        </div>
      )}

      <div className="lodi-card p-5" data-testid="opname-history">
        <h3 className="font-display font-bold mb-4">Riwayat Opname</h3>
        {history.length === 0 ? (
          <p className="text-sm text-muted-foreground">Belum ada opname.</p>
        ) : (
          <div className="space-y-2">
            {history.slice(0, 10).map((h) => (
              <div key={h.id} className="bg-elevated rounded-lg px-4 py-3 text-sm flex flex-wrap items-center justify-between gap-2" data-testid={`opname-history-${h.id}`}>
                <div>
                  <p className="font-semibold">{h.date_wib} — {branchLabel(h.branch)}</p>
                  <p className="text-xs text-muted-foreground">oleh {h.created_by} • {h.items.length} item {h.adjusted ? '• stok disesuaikan' : ''}</p>
                </div>
                <div className="text-xs font-semibold">
                  {h.items.filter((i) => i.variance !== 0).length === 0
                    ? <span className="text-emerald-400">Sesuai semua</span>
                    : <span className="text-amber-400">{h.items.filter((i) => i.variance !== 0).length} selisih</span>}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
